import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CursorType } from "@/store/globalSettingsSlice";
import { useIframePoolStore } from "./iframePoolStore";
import { useIframeStore } from "@/components/Canvas/stores/iframeStore";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { useActiveCanvas } from "@/utils/utils";
import { useAllStoryboardsWithErrorOverlays } from "../Storyboard/StaticIframe/hooks/useStoryboardState";
import { useHoveredNavTreeNode } from "@/components/Canvas/stores/canvasStore";

interface IframePosition {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const DriveModeIframeOverlay: React.FC = () => {
  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType,
  );
  const isDriveMode = cursorType === CursorType.Drive;

  // Clear drive position when drive mode is disabled
  useEffect(() => {
    if (!isDriveMode) {
      const { tempobookIframe, setIframeDrivePosition } =
        useIframePoolStore.getState();
      if (tempobookIframe?.drivePosition) {
        setIframeDrivePosition(undefined);
      }
    }
  }, [isDriveMode]);

  const activeCanvas = useActiveCanvas();
  const { hoveredElement } = useHoveredNavTreeNode();

  const [iframePosition, setIframePosition] = useState<IframePosition | null>(
    null,
  );
  const [activeStoryboardId, setActiveStoryboardId] = useState<string | null>(
    null,
  );
  const [prevActiveFileKey, setPrevActiveFileKey] = useState<string | null>(
    null,
  );

  const applicationStoryboards = useIframePoolStore(
    (state) => state.applicationStoryboards,
  );
  const tempobookIframe = useIframePoolStore((state) => state.tempobookIframe);
  const registeredStoryboards = useIframePoolStore(
    (state) => state.registeredStoryboards,
  );

  const storyboardsWithOverlays = useAllStoryboardsWithErrorOverlays();

  /**
   * Set the show loading for application storyboards when drive mode is enabled
   *
   * The actual iframe will cover the loading state
   */
  useEffect(() => {
    if (!activeCanvas) {
      return;
    }

    activeCanvas.storyboards?.forEach((storyboard) => {
      if (
        storyboard.type === STORYBOARD_TYPES.APPLICATION &&
        applicationStoryboards[storyboard.id]
      ) {
        useIframePoolStore
          .getState()
          .setShowLoading(storyboard.id, isDriveMode);
      }
    });
  }, [isDriveMode, activeCanvas?.storyboards]);

  // Handle non-application storyboards - show iframe on hover
  useEffect(() => {
    if (!isDriveMode || hoveredElement?.isEmpty() || !activeCanvas) {
      return;
    }

    const hoveredStoryboard = activeCanvas.storyboards?.find(
      (s) => s.id === hoveredElement.storyboardId,
    );

    if (!hoveredStoryboard) {
      const { tempobookIframe, setIframeDrivePosition } =
        useIframePoolStore.getState();
      if (tempobookIframe?.drivePosition) {
        setIframeDrivePosition(undefined);
      }
      return;
    }

    if (hoveredStoryboard.type === STORYBOARD_TYPES.APPLICATION) {
      // Don't handle application storyboards here
      return;
    }

    // Calculate position for the hovered storyboard
    const position = {
      left: hoveredStoryboard.x,
      top: hoveredStoryboard.y,
      width: hoveredStoryboard.width,
      height: hoveredStoryboard.height,
    };
    setIframePosition(position);
    setActiveStoryboardId(hoveredElement.storyboardId);
  }, [isDriveMode, hoveredElement, activeCanvas]);

  // Update position when different storyboard is hovered
  useEffect(() => {
    if (!isDriveMode || !activeStoryboardId || !activeCanvas) return;

    const storyboard = activeCanvas.storyboards?.find(
      (s) => s.id === activeStoryboardId,
    );
    if (storyboard && storyboard.type !== STORYBOARD_TYPES.APPLICATION) {
      const position = {
        left: storyboard.x,
        top: storyboard.y,
        width: storyboard.width,
        height: storyboard.height,
      };
      if (position) {
        setIframePosition(position);
      }
    }
  }, [activeStoryboardId, isDriveMode]);

  const iframeIsReady = tempobookIframe?.status === "ready";

  const targetHasOverlay = storyboardsWithOverlays.has(
    activeStoryboardId || "",
  );

  // Update iframe positioning for non-application storyboards
  useEffect(() => {
    const setIframeDrivePosition =
      useIframePoolStore.getState().setIframeDrivePosition;

    const activeStoryboard = activeCanvas?.storyboards?.find(
      (s) => s.id === activeStoryboardId,
    );

    if (!isDriveMode || !iframePosition || !activeStoryboard) {
      // Clear drive position for the tempobook iframe when not hovering
      const currentIframe = useIframePoolStore.getState().tempobookIframe;
      if (currentIframe?.drivePosition) {
        setIframeDrivePosition(undefined);
      }
      return;
    }

    // Only show iframe if ready or if there's an overlay
    const shouldShowIframe = iframeIsReady || targetHasOverlay;
    if (shouldShowIframe && tempobookIframe) {
      const newPosition = {
        left: iframePosition.left,
        top: iframePosition.top,
        width: iframePosition.width,
        height: iframePosition.height,
        visible: true,
      };

      // Only update if position changed
      const currentPosition = tempobookIframe.drivePosition;
      const positionChanged =
        !currentPosition ||
        currentPosition.left !== newPosition.left ||
        currentPosition.top !== newPosition.top ||
        currentPosition.width !== newPosition.width ||
        currentPosition.height !== newPosition.height ||
        currentPosition.visible !== newPosition.visible;

      if (positionChanged) {
        setIframeDrivePosition(newPosition);
      }
    } else {
      // Hide the iframe while it's not ready
      setIframeDrivePosition(undefined);
    }
  }, [
    isDriveMode,
    activeStoryboardId,
    iframeIsReady,
    iframePosition,
    activeCanvas?.storyboards,
    targetHasOverlay,
    tempobookIframe?.id,
  ]);

  // Send drive mode storyboard isolation message via MessageChannel
  useEffect(() => {
    const port = useIframeStore.getState().iframeMessagingPort;
    if (!port) return;

    if (isDriveMode && activeStoryboardId) {
      // Get fileKey from registeredStoryboards
      const registeredSb = registeredStoryboards[activeStoryboardId];
      const fileKey = registeredSb?.fileKey || null;

      // Only update if fileKey changed
      if (fileKey !== prevActiveFileKey) {
        setPrevActiveFileKey(fileKey);

        // Send message to iframe via MessageChannel
        if (fileKey) {
          port.postMessage({
            id: "SET_DRIVE_MODE_STORYBOARD",
            type: "executeFunction",
            payload: {
              functionName: "setDriveModeStoryboard",
              args: [fileKey],
            },
          });
        }
      }
    } else if (!isDriveMode && prevActiveFileKey !== null) {
      // Reset when exiting drive mode
      setPrevActiveFileKey(null);
      port.postMessage({
        id: "SET_DRIVE_MODE_STORYBOARD",
        type: "executeFunction",
        payload: {
          functionName: "setDriveModeStoryboard",
          args: [null],
        },
      });
    }
  }, [
    isDriveMode,
    activeStoryboardId,
    registeredStoryboards,
    prevActiveFileKey,
  ]);

  // Reset drive mode on component unmount
  useEffect(() => {
    return () => {
      const port = useIframeStore.getState().iframeMessagingPort;
      if (!port) return;
      port.postMessage({
        id: "SET_DRIVE_MODE_STORYBOARD",
        type: "executeFunction",
        payload: {
          functionName: "setDriveModeStoryboard",
          args: [null],
        },
      });
    };
  }, []);

  return null; // This component only manages iframe positioning, doesn't render anything
};
